import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ListsState } from '@alltrails/lists/slices/lists';
import { completedListId, getItemsInListOfType, isSavedListId } from '@alltrails/lists/utils/listUtils';
import ExploreServiceRecordIds from '../types/ExploreServiceRecordIds';
import TrailEngagement from '../types/TrailEngagement';

const intersection = (a: number[], b: number[]): number[] => {
  const setB = new Set(b);
  return a.filter(id => setB.has(id));
};

const difference = (a: number[], b: number[]): number[] => {
  const setB = new Set(b);
  return a.filter(id => !setB.has(id));
};

const union = (a: number[], b: number[]): number[] => {
  return Array.from(new Set([...a, ...b]));
};

const useTrailEngagementRecordIds = (trailEngagement: TrailEngagement[]) => {
  const listItems = useSelector((state: { lists: ListsState }) => state.lists.listItems);

  const completedTrailIds = useMemo(() => {
    return Object.keys(getItemsInListOfType(listItems, completedListId, 'trail')).map(id => Number(id));
  }, [listItems]);

  const savedTrailIds = useMemo(() => {
    const trailIdsSet = new Set<number>();

    if (!listItems) {
      return [];
    }

    // Iterate through all lists, excluding completed and verified completed
    const listIds = Object.keys(listItems)
      .map(id => Number(id))
      .filter(isSavedListId);

    // Collect all trail IDs from these lists
    listIds.forEach(listId => {
      const trailsInList = getItemsInListOfType(listItems, listId, 'trail');
      Object.keys(trailsInList).forEach(trailId => {
        trailIdsSet.add(Number(trailId));
      });
    });

    return Array.from(trailIdsSet);
  }, [listItems]);

  const recordIds = useMemo<[ExploreServiceRecordIds] | undefined>(() => {
    if (!trailEngagement?.length) {
      return undefined;
    }

    const hasCompleted = trailEngagement.includes('completed');
    const hasNotCompleted = trailEngagement.includes('not_completed');
    const hasSaved = trailEngagement.includes('saved');
    const hasNotSaved = trailEngagement.includes('not_saved');

    // Single option combinations
    if (hasCompleted && !hasNotCompleted && !hasSaved && !hasNotSaved) {
      return [{ ids: completedTrailIds, mode: 'and', recordType: 'trail' }];
    } else if (!hasCompleted && !hasNotCompleted && hasSaved && !hasNotSaved) {
      return [{ ids: savedTrailIds, mode: 'and', recordType: 'trail' }];
    } else if (!hasCompleted && hasNotCompleted && !hasSaved && !hasNotSaved) {
      return [{ ids: completedTrailIds, mode: 'not', recordType: 'trail' }];
    } else if (!hasCompleted && !hasNotCompleted && !hasSaved && hasNotSaved) {
      return [{ ids: savedTrailIds, mode: 'not', recordType: 'trail' }];
    }
    // Double option combinations
    else if (hasCompleted && !hasNotCompleted && hasSaved && !hasNotSaved) {
      return [{ ids: intersection(completedTrailIds, savedTrailIds), mode: 'and', recordType: 'trail' }];
    } else if (hasCompleted && hasNotCompleted && !hasSaved && !hasNotSaved) {
      return [{ ids: [], mode: 'not', recordType: 'trail' }];
    } else if (!hasCompleted && hasNotCompleted && hasSaved && !hasNotSaved) {
      return [{ ids: difference(savedTrailIds, completedTrailIds), mode: 'and', recordType: 'trail' }];
    } else if (hasCompleted && !hasNotCompleted && !hasSaved && hasNotSaved) {
      return [{ ids: difference(completedTrailIds, savedTrailIds), mode: 'and', recordType: 'trail' }];
    } else if (!hasCompleted && !hasNotCompleted && hasSaved && hasNotSaved) {
      return [{ ids: [], mode: 'not', recordType: 'trail' }];
    } else if (!hasCompleted && hasNotCompleted && !hasSaved && hasNotSaved) {
      return [{ ids: union(completedTrailIds, savedTrailIds), mode: 'not', recordType: 'trail' }];
    }
    // Triple option combinations
    else if (hasCompleted && hasNotCompleted && hasSaved && !hasNotSaved) {
      return [{ ids: savedTrailIds, mode: 'and', recordType: 'trail' }];
    } else if (hasCompleted && !hasNotCompleted && hasSaved && hasNotSaved) {
      return [{ ids: completedTrailIds, mode: 'and', recordType: 'trail' }];
    } else if (hasCompleted && hasNotCompleted && !hasSaved && hasNotSaved) {
      return [{ ids: savedTrailIds, mode: 'not', recordType: 'trail' }];
    } else if (!hasCompleted && hasNotCompleted && hasSaved && hasNotSaved) {
      return [{ ids: completedTrailIds, mode: 'not', recordType: 'trail' }];
    }

    // Quadruple option combinations
    else if (hasCompleted && hasNotCompleted && hasSaved && hasNotSaved) {
      return [{ ids: [], mode: 'not', recordType: 'trail' }];
    }

    return undefined;
  }, [completedTrailIds, trailEngagement, savedTrailIds]);

  return recordIds;
};

export default useTrailEngagementRecordIds;
